import { useState } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import CardMedia from '@mui/material/CardMedia';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import MainCard from 'mantis/components/MainCard';

import useConfig from 'src/hooks/useConfig';
import { MenuOrientation, ThemeDirection } from 'src/config';

// assets
import defaultLayout from 'assets/images/customization/default.svg';
import rtlLayout from 'assets/images/customization/rtl.svg';
import miniMenu from 'assets/images/customization/mini-menu.svg';
import useInterface from 'src/hooks/useInterface';

// ==============================|| CUSTOMIZATION - LAYOUT ||============================== //

export default function ThemeLayout() {
	const theme = useTheme();
	const downLG = useMediaQuery(theme.breakpoints.down('lg'));
	const { isDrawerOpen: drawerOpen, onToggleDrawer: handlerDrawerOpen } =
		useInterface();
	const {
		hasRTL,
		miniDrawer,
		themeDirection,
		onChangeThemeLayout,
		onChangeMiniDrawer,
		menuOrientation,
	} = useConfig();

	let initialTheme = 'default';
	if (miniDrawer === true) initialTheme = 'mini';
	if (themeDirection === ThemeDirection.RTL && !miniDrawer)
		initialTheme = 'rtl';

	const [value, setValue] = useState(initialTheme);
	const handleRadioChange = (event) => {
		const newValue = event.target.value;
		setValue(newValue);
		switch (newValue) {
			case 'mini':
				onChangeMiniDrawer(true);
				if (drawerOpen) {
					handlerDrawerOpen(false);
				}
				break;
			case ThemeDirection.RTL:
				onChangeThemeLayout(ThemeDirection.RTL, false);
				if (!drawerOpen) {
					handlerDrawerOpen(true);
				}
				break;
			case 'default':
				onChangeThemeLayout(ThemeDirection.LTR, false);
				if (!drawerOpen) {
					handlerDrawerOpen(true);
				}
				break;
			default:
				handlerDrawerOpen(true);
				break;
		}
	};

	return (
		<RadioGroup
			row
			aria-label="payment-card"
			name="payment-card"
			value={value}
			onChange={handleRadioChange}
		>
			<Grid container spacing={1.75} sx={{ ml: 0 }}>
				<Grid item>
					<FormControlLabel
						value="default"
						control={<Radio sx={{ display: 'none' }} />}
						sx={{
							display: 'flex',
							'& .MuiFormControlLabel-label': { flex: 1 },
						}}
						label={
							<MainCard
								content={false}
								sx={{
									bgcolor:
										value === 'default'
											? 'primary.lighter'
											: 'secondary.lighter',
									p: 1,
								}}
								border={false}
								{...(value === 'default' && {
									boxShadow: true,
									shadow: theme.customShadows.primary,
								})}
							>
								<Stack spacing={1.25} alignItems="center">
									<CardMedia
										component="img"
										src={defaultLayout}
										alt="Vertical"
										sx={{ borderRadius: 1, width: 64, height: 64 }}
									/>
									<Typography variant="caption">Default</Typography>
								</Stack>
							</MainCard>
						}
					/>
				</Grid>

				{(menuOrientation === MenuOrientation.VERTICAL || downLG) && (
					<Grid item>
						<FormControlLabel
							value="mini"
							control={<Radio sx={{ display: 'none' }} />}
							sx={{
								display: 'flex',
								'& .MuiFormControlLabel-label': { flex: 1 },
							}}
							label={
								<MainCard
									content={false}
									sx={{
										bgcolor:
											value === 'mini'
												? 'primary.lighter'
												: 'secondary.lighter',
										p: 1,
									}}
									border={false}
									{...(value === 'mini' && {
										boxShadow: true,
										shadow: theme.customShadows.primary,
									})}
								>
									<Stack spacing={1.25} alignItems="center">
										<CardMedia
											component="img"
											src={miniMenu}
											alt="Vertical"
											sx={{ borderRadius: 1, width: 64, height: 64 }}
										/>
										<Typography variant="caption">Mini Drawer</Typography>
									</Stack>
								</MainCard>
							}
						/>
					</Grid>
				)}

				{hasRTL && (
					<Grid item>
						<FormControlLabel
							value="rtl"
							control={<Radio sx={{ display: 'none' }} />}
							sx={{
								display: 'flex',
								'& .MuiFormControlLabel-label': { flex: 1 },
							}}
							label={
								<MainCard
									content={false}
									sx={{
										bgcolor:
											value === ThemeDirection.RTL && !miniDrawer
												? 'primary.lighter'
												: 'secondary.lighter',
										p: 1,
									}}
									border={false}
									{...(value === ThemeDirection.RTL &&
										!miniDrawer && {
										boxShadow: true,
										shadow: theme.customShadows.primary,
									})}
								>
									<Stack spacing={1.25} alignItems="center">
										<CardMedia
											component="img"
											src={rtlLayout}
											alt="Vertical"
											sx={{ borderRadius: 1, width: 64, height: 64 }}
										/>
										<Typography variant="caption">RTL</Typography>
									</Stack>
								</MainCard>
							}
						/>
					</Grid>
				)}
			</Grid>
		</RadioGroup>
	);
}
